import * as React from "react";
import _ from "lodash";
import { Form, Field } from "react-final-form";

import { useAuth } from "services/auth-provider";
import useListFilters, { ListFiltersState } from "utils/hooks/useListFilters";
import { UserRole } from "modules/users/users.constants";
import authClient from "services/auth";
import { useCompaniesListPublic } from "modules/companies/provider";

import useStyles from "./group-tree-filter.styles";
import GroupTreeControl from "./group-tree-control";

type Props = {
  clearLabel?: string;
  label?: string;
  listFilters: ListFiltersState;
  name: string;
  allowMultiple?: boolean;
};

const GroupTreeFilter: React.FC<Props> = props => {
  const { checkAccess } = useAuth();
  const [isTokenRefreshed, setTokenRefreshed] = React.useState(false);
  const isTenantAdmin = checkAccess({ roles: [UserRole.TENANT_ADMIN] });

  React.useEffect(() => {
    if (isTenantAdmin) {
      authClient.refreshTokens().finally(() => {
        setTokenRefreshed(true);
      });
    }
  }, [isTenantAdmin]);

  const [isFetched, setIsFetched] = React.useState(false);
  const groupsListFilters = useListFilters({
    pagination: { rowsPerPage: 0 },
    orderBy: ["name,ASC"]
  });

  const groups = useCompaniesListPublic({
    variables: {
      listFilters: groupsListFilters,
      ...(isTenantAdmin && { onlyOwn: true })
    },
    skip: isFetched || (isTenantAdmin && !isTokenRefreshed)
  });

  const options = React.useMemo(() => {
    if (groups.data) {
      setIsFetched(true);
      return groups.data.map(({ id, name, parent }) => ({
        label: `${name} (${"company"}) `,
        value: id,
        parent: isTenantAdmin
          ? groups.data!.some(({ id }) => id === parent)
            ? parent
            : null
          : parent
      }));
    }
    return [];
  }, [groups.data, isTenantAdmin]);

  const currentValue = _.get(
    props.listFilters,
    `filters.${props.name}`,
    props.allowMultiple ? [] : ""
  );
  const onFilterHandler = React.useCallback(
    newValues => {
      if (props.allowMultiple) {
        if (!_.isEqual(currentValue, newValues)) {
          if (newValues.length === 0) {
            props.listFilters.setFilter(props.name, undefined);
          } else {
            props.listFilters.setFilter(props.name, newValues);
          }
        }
      } else {
        if (newValues[0] !== currentValue) {
          props.listFilters.setFilter(props.name, newValues[0]);
        }
      }
    },
    [currentValue, props.allowMultiple, props.listFilters, props.name]
  );
  const classes = useStyles();

  return (
    <div className={classes.wrapper}>
      <Form
        onSubmit={() => {}}
        render={() => (
          <Field
            label={props.label}
            name={props.name}
            allowMultiple={props.allowMultiple}
            onFilter={onFilterHandler}
            component={GroupTreeControl}
            listFilters={groupsListFilters}
            options={options}
          />
        )}
      />
    </div>
  );
};

export default GroupTreeFilter;
